// ---------------------------------------------------------------------------
// CreativesTable — TanStack Table v8 grid for creative hosting rows.
//
// Columns: id (short), label, type badge, mime, size (human), created_at.
// Owned by OPUS-D16. No writes / uploads from this file — the page holds
// the Upload CTA.
// ---------------------------------------------------------------------------
import { useMemo } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import {
  formatBytes,
  formatCreatedAt,
  type CreativeView,
  type CreativeType,
} from './api';

// Type badge palette — all light-theme, zinc borders, subtle tints. NO
// orange / purple. Blue / emerald / sky family only per brand lock.
const BADGE_TINT: Record<CreativeType, string> = {
  banner: 'bg-blue-50 text-blue-700 border-blue-200',
  video: 'bg-sky-50 text-sky-700 border-sky-200',
  html: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  native: 'bg-teal-50 text-teal-700 border-teal-200',
  email: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  lp: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  drive_link: 'bg-zinc-50 text-zinc-700 border-zinc-200',
  other: 'bg-zinc-50 text-zinc-600 border-zinc-200',
};

function TypeBadge({ type }: { type: CreativeType }) {
  const tint = BADGE_TINT[type] ?? BADGE_TINT.other;
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${tint}`}
    >
      {type}
    </span>
  );
}

function shortId(id: string): string {
  if (id.length <= 10) return id;
  return `${id.slice(0, 8)}…`;
}

export type CreativesTableProps = {
  data: CreativeView[];
  isLoading?: boolean;
  emptyLabel?: string;
};

export function CreativesTable({
  data,
  isLoading = false,
  emptyLabel = 'No creatives yet for this campaign.',
}: CreativesTableProps) {
  const columns = useMemo<ColumnDef<CreativeView>[]>(
    () => [
      {
        id: 'id',
        header: 'ID',
        accessorKey: 'id',
        cell: (ctx) => (
          <span
            className="font-mono text-xs text-zinc-500"
            title={ctx.getValue<string>()}
          >
            {shortId(ctx.getValue<string>())}
          </span>
        ),
      },
      {
        id: 'label',
        header: 'Label',
        accessorKey: 'label',
        cell: (ctx) => (
          <span className="text-sm font-medium text-zinc-900">
            {ctx.getValue<string>() || '—'}
          </span>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        accessorKey: 'type',
        cell: (ctx) => <TypeBadge type={ctx.getValue<CreativeType>()} />,
      },
      {
        id: 'mime',
        header: 'MIME',
        accessorKey: 'mime',
        cell: (ctx) => {
          const m = ctx.getValue<string>();
          return (
            <span className="font-mono text-xs text-zinc-600">{m || '—'}</span>
          );
        },
      },
      {
        id: 'size',
        header: 'Size',
        accessorKey: 'size_bytes',
        cell: (ctx) => (
          <span className="tabular-nums text-sm text-zinc-700">
            {formatBytes(ctx.getValue<number>())}
          </span>
        ),
      },
      {
        id: 'created_at',
        header: 'Created',
        accessorKey: 'created_at',
        cell: (ctx) => (
          <span className="text-sm text-zinc-600">
            {formatCreatedAt(ctx.getValue<number>())}
          </span>
        ),
      },
    ],
    [],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm">
      <table className="w-full border-collapse">
        <thead className="bg-zinc-50">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className="border-b border-zinc-200 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500"
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-8 text-center text-sm text-zinc-400"
              >
                Loading creatives…
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-8 text-center text-sm text-zinc-400"
              >
                {emptyLabel}
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50/60"
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-4 py-3 align-middle">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

